import { useLayoutEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigationType } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { AdminAuthProvider } from "@/context/AdminAuthContext";
import { StoreProvider } from "@/context/StoreContext";
import { ProtectedRoute, AdminRoute } from "@/components/RouteGuards";
import AnalyticsConsent from "@/components/storefront/AnalyticsConsent";

import Layout from "@/components/storefront/Layout";
import Home from "@/pages/storefront/Home";
import Shop from "@/pages/storefront/Shop";
import ProductDetail from "@/pages/storefront/ProductDetail";
import Cart from "@/pages/storefront/Cart";
import Checkout from "@/pages/storefront/Checkout";
import OrderSuccess from "@/pages/storefront/OrderSuccess";
import TrackOrder from "@/pages/storefront/TrackOrder";
import Brands from "@/pages/storefront/Brands";
import Categories from "@/pages/storefront/Categories";
import Wishlist from "@/pages/storefront/Wishlist";
import Services from "@/pages/storefront/Services";
import StaticPage from "@/pages/storefront/StaticPage";
import NotFound from "@/pages/storefront/NotFound";
import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";
import Account from "@/pages/account/Account";
import AccountProfile from "@/pages/account/AccountProfile";
import AccountAddresses from "@/pages/account/AccountAddresses";
import AccountOrders from "@/pages/account/AccountOrders";
import AccountOrderDetail from "@/pages/account/AccountOrderDetail";
import AccountReviews from "@/pages/account/AccountReviews";

import AdminLogin from "@/pages/admin/AdminLogin";
import AdminLayout from "@/components/admin/AdminLayout";
import Dashboard from "@/pages/admin/Dashboard";
import AdminProducts from "@/pages/admin/AdminProducts";
import ProductForm from "@/pages/admin/ProductForm";
import AdminCategories from "@/pages/admin/AdminCategories";
import AdminBrands from "@/pages/admin/AdminBrands";
import AdminInventory from "@/pages/admin/AdminInventory";
import AdminInventoryHistory from "@/pages/admin/AdminInventoryHistory";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminOrderDetail from "@/pages/admin/AdminOrderDetail";
import AdminCustomers from "@/pages/admin/AdminCustomers";
import AdminCustomerDetail from "@/pages/admin/AdminCustomerDetail";
import AdminCoupons from "@/pages/admin/AdminCoupons";
import AdminAutoDiscounts from "@/pages/admin/AdminAutoDiscounts";
import AdminReviews from "@/pages/admin/AdminReviews";
import AdminHomepage from "@/pages/admin/AdminHomepage";
import AdminBanners from "@/pages/admin/AdminBanners";
import AdminShipping from "@/pages/admin/AdminShipping";
import AdminBilling from "@/pages/admin/AdminBilling";
import AdminReturns from "@/pages/admin/AdminReturns";
import AdminPayments from "@/pages/admin/AdminPayments";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminServices from "@/pages/admin/AdminServices";
import AdminAuditLogs from "@/pages/admin/AdminAuditLogs";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminIntegrations from "@/pages/admin/AdminIntegrations";

const STATIC = [
  ["about", "About Us"], ["contact", "Contact"], ["store", "Our Store"],
  ["privacy-policy", "Privacy Policy"], ["terms", "Terms & Conditions"],
  ["shipping-policy", "Shipping Policy"], ["cancellation-policy", "Cancellation Policy"],
  ["return-refund-policy", "Return & Refund Policy"],
];

function ScrollToTop() {
  const { pathname } = useLocation();
  const navigationType = useNavigationType();

  useLayoutEffect(() => {
    if (navigationType !== "POP") window.scrollTo(0, 0);
  }, [pathname, navigationType]);

  return null;
}

function App() {
  return (
    <AuthProvider>
      <AdminAuthProvider>
      <StoreProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Toaster position="top-center" richColors />
          <AnalyticsConsent />
          <Routes>
            <Route element={<Layout />}>
              <Route path="/" element={<Home />} />
              <Route path="/shop" element={<Shop />} />
              <Route path="/products" element={<Shop />} />
              <Route path="/products/:category" element={<Shop />} />
              <Route path="/product/:slug" element={<ProductDetail />} />
              <Route path="/search" element={<Shop mode="search" />} />
              <Route path="/brands" element={<Brands />} />
              <Route path="/brand/:slug" element={<Shop mode="brand" />} />
              <Route path="/categories" element={<Categories />} />
              <Route path="/category/:slug" element={<Shop mode="category" />} />
              <Route path="/cart" element={<Cart />} />
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/order-success/:id" element={<OrderSuccess />} />
              <Route path="/track-order" element={<TrackOrder />} />
              <Route path="/wishlist" element={<Wishlist />} />
              <Route path="/services" element={<Services />} />
              <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>}>
                <Route index element={<AccountProfile />} />
                <Route path="profile" element={<AccountProfile />} />
                <Route path="addresses" element={<AccountAddresses />} />
                <Route path="orders" element={<AccountOrders />} />
                <Route path="orders/:id" element={<AccountOrderDetail />} />
                <Route path="reviews" element={<AccountReviews />} />
              </Route>
              {STATIC.map(([slug, title]) => (
                <Route key={slug} path={`/${slug}`} element={<StaticPage slug={slug} title={title} />} />
              ))}
            </Route>

            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
              <Route index element={<Dashboard />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="products" element={<AdminProducts />} />
              <Route path="products/new" element={<ProductForm />} />
              <Route path="products/:id/edit" element={<ProductForm />} />
              <Route path="categories" element={<AdminCategories />} />
              <Route path="brands" element={<AdminBrands />} />
              <Route path="inventory" element={<AdminInventory />} />
              <Route path="inventory/history" element={<AdminInventoryHistory />} />
              <Route path="orders" element={<AdminOrders />} />
              <Route path="orders/:id" element={<AdminOrderDetail />} />
              <Route path="customers" element={<AdminCustomers />} />
              <Route path="customers/:id" element={<AdminCustomerDetail />} />
              <Route path="coupons" element={<AdminCoupons />} />
              <Route path="auto-discounts" element={<AdminAutoDiscounts />} />
              <Route path="reviews" element={<AdminReviews />} />
              <Route path="services" element={<AdminServices />} />
              <Route path="homepage" element={<AdminHomepage />} />
              <Route path="banners" element={<AdminBanners />} />
              <Route path="shipping" element={<AdminShipping />} />
              <Route path="returns" element={<AdminReturns />} />
              <Route path="billing" element={<AdminBilling />} />
              <Route path="payments" element={<AdminPayments />} />
              <Route path="analytics" element={<AdminAnalytics />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="security" element={<AdminUsers />} />
              <Route path="audit-logs" element={<AdminAuditLogs />} />
              <Route path="settings" element={<AdminSettings />} />
              <Route path="integrations" element={<AdminIntegrations />} />
            </Route>

            <Route path="*" element={<Layout />}>
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </StoreProvider>
      </AdminAuthProvider>
    </AuthProvider>
  );
}

export default App;
